import { supabase } from '../supabaseClient.js';

// Calls against a TikCare backend, with the caller's Supabase session attached.
// Two backends are fronted by this helper today: the RAG/connect backend
// (access codes) and the Stripe subscription backend
// (REACT_APP_STRIPE_SUBSCRIPTION_URL). Both verify the JWT and derive the
// acting user from it, so identity/money request bodies must NOT carry
// userId / customerId / priceId.
//
// The backend verifies the JWT against Supabase Auth, so a token whose session
// was revoked server-side (password changed on another device, admin reset,
// session expiry) still looks valid to getSession() but returns 401 here.
// Surfacing that as a bare "Authentication required" strands the user on a
// page that looks broken, so: refresh once, and if the session is genuinely
// gone, sign out and send them to login with a reason.
export async function authedFetch(url, options = {}, diagnostics) {
  const observe = (stage, task, valid, failureCode) => diagnostics ? diagnostics.run(stage, task, valid, failureCode) : task();
  const authFailure = value => value?.error ? 'auth_error' : 'session_missing';
  const send = (token, stage) => observe(stage, () =>
    fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token || ''}`,
      },
    }), response => response.ok, response => Number.isInteger(response.status) && response.status >= 100 && response.status <= 599 ? `http_${response.status}` : 'http_error');

  const { data: { session } = {} } = await observe('session_auth', () => supabase.auth.getSession(), value => !value?.error && Boolean(value?.data?.session), authFailure);
  let response = await send(session?.access_token, 'session_api');
  if (response.status !== 401) return response;

  const { data, error } = await observe('session_auth_refresh', () => supabase.auth.refreshSession(), value => !value?.error && Boolean(value?.data?.session), authFailure);
  if (!error && data?.session?.access_token) {
    response = await send(data.session.access_token, 'session_api_after_refresh');
    if (response.status !== 401) return response;
  }

  await supabase.auth.signOut();
  window.location.assign('/login?reason=session_expired');
  throw new Error('Your session has expired. Please sign in again.');
}

export default authedFetch;
